import { toast } from "@redotech/redo-web/alert";
import { createQueryClient } from "@redotech/redo-web/rpc-query/react-query-config";
import { MutationCache, QueryCache } from "@tanstack/react-query";
import { Cause, Runtime } from "effect";

export function shouldShowQueryErrorToast(query: {
  meta?: { showErrorToast?: unknown };
  state: { status?: string; error?: unknown };
  options?: unknown;
}): boolean {
  if (!query.meta?.showErrorToast) {
    return false;
  }
  if (isAbortError(query.state.error)) {
    return false;
  }
  if (queryWillKeepRefetching(query)) {
    return false;
  }
  return true;
}

function queryWillKeepRefetching(query: {
  state: { status?: string; error?: unknown };
  options?: unknown;
}): boolean {
  const interval = getRefetchInterval(query.options);
  if (typeof interval !== "function") {
    return false;
  }
  const resolved = interval(query);
  return typeof resolved === "number" && resolved > 0;
}

function isAbortError(error: unknown): boolean {
  if (isAbortErrorValue(error)) {
    return true;
  }
  if (!Runtime.isFiberFailure(error)) {
    return false;
  }
  const cause = error[Runtime.FiberFailureCauseId];
  for (const inner of [...Cause.defects(cause), ...Cause.failures(cause)]) {
    if (isAbortErrorValue(inner)) {
      return true;
    }
  }
  return false;
}

function isAbortErrorValue(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    error.name === "AbortError"
  );
}

function getRefetchInterval(options: unknown): unknown {
  if (typeof options !== "object" || options === null) {
    return undefined;
  }
  if (!("refetchInterval" in options)) {
    return undefined;
  }
  return options.refetchInterval;
}

export function extractUserMessage(error: unknown): string {
  if (Runtime.isFiberFailure(error)) {
    const cause = error[Runtime.FiberFailureCauseId];
    for (const defect of Cause.defects(cause)) {
      if (defect instanceof Error && defect.message) {
        return defect.message;
      }
    }
    // FiberFailure with no usable defect message — don't fall through to bare
    // Error check since FiberFailure extends Error and has its own .message.
    return "An unexpected error occurred. Please try again.";
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "An unexpected error occurred. Please try again.";
}

export const merchantQueryClient = createQueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (shouldShowQueryErrorToast(query)) {
        toast(extractUserMessage(error), { variant: "error" });
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.options.meta?.showErrorToast) {
        toast(extractUserMessage(error), { variant: "error" });
      }
    },
  }),
});
